import DeleteSweepIcon from '@mui/icons-material/DeleteSweep';
import EditIcon from '@mui/icons-material/Edit';
import PeopleIcon from '@mui/icons-material/People';
import TouchAppIcon from '@mui/icons-material/TouchApp';
import TrendingUpIcon from '@mui/icons-material/TrendingUp';
import {
    Alert,
    Box,
    Button,
    Card,
    CardContent,
    Chip,
    CircularProgress,
    Dialog,
    DialogActions,
    DialogContent,
    DialogContentText,
    DialogTitle,
    Grid,
    Paper,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Tooltip,
    Typography
} from '@mui/material';
import { useEffect, useMemo, useState } from 'react';
import UsageTrackingService from '../../services/UsageTrackingService';

const dateRangeToStartDate = (dateRange) => {
    const now = new Date();
    switch (dateRange) {
        case 'last24hours':
            return new Date(now.getTime() - 24 * 60 * 60 * 1000);
        case 'last7days':
            return new Date(now.getTime() - 7 * 24 * 60 * 60 * 1000);
        case 'last90days':
            return new Date(now.getTime() - 90 * 24 * 60 * 60 * 1000);
        case 'all':
            return null;
        case 'last30days':
        default:
            return new Date(now.getTime() - 30 * 24 * 60 * 60 * 1000);
    }
};

export default function FeatureUsageStats({ dateRange = 'last30days', isAdmin = false }) {
    const [rows, setRows] = useState([]);
    const [loading, setLoading] = useState(true);
    const [clearDialogOpen, setClearDialogOpen] = useState(false);
    const [clearing, setClearing] = useState(false);
    const [clearError, setClearError] = useState(null);
    const [clearSuccess, setClearSuccess] = useState(false);

    const fetchUsage = async () => {
        setLoading(true);
        try {
            const startDate = dateRangeToStartDate(dateRange);
            const data = await UsageTrackingService.getUsageStats(
                startDate ? startDate.toISOString() : null,
                new Date().toISOString()
            );
            setRows(Array.isArray(data) ? data : []);
        } catch (error) {
            console.error('Error fetching usage stats:', error);
            setRows([]);
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        fetchUsage();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [dateRange]);

    const handleClearData = async () => {
        setClearing(true);
        setClearError(null);
        try {
            await UsageTrackingService.clearAllUsageData();
            setClearSuccess(true);
            setTimeout(() => {
                setClearDialogOpen(false);
                setClearSuccess(false);
                fetchUsage();
            }, 1500);
        } catch (error) {
            console.error('Failed to clear usage data:', error);
            setClearError(error.response?.data?.message || 'Failed to clear usage data. Please try again.');
        } finally {
            setClearing(false);
        }
    };

    const sortedRows = useMemo(() => [...rows].sort((a, b) => (b.opens || 0) - (a.opens || 0)), [rows]);

    const totals = useMemo(() => {
        const totalOpens = rows.reduce((sum, row) => sum + (row.opens || 0), 0);
        const uniqueUsers = new Set();
        rows.forEach((row) => {
            if (row.uniqueUsers) uniqueUsers.add(row.componentName);
        });
        const mostOpened = sortedRows[0];
        return {
            totalOpens,
            uniqueUsersApprox: rows.reduce((sum, row) => sum + (row.uniqueUsers || 0), 0),
            mostOpened,
        };
    }, [rows, sortedRows]);

    if (loading) {
        return (
            <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
                <CircularProgress />
            </Box>
        );
    }

    return (
        <Box>
            {isAdmin && (
                <Box sx={{ display: 'flex', justifyContent: 'flex-end', mb: 2 }}>
                    <Tooltip title="Clear all usage tracking data">
                        <Button
                            variant="outlined"
                            color="error"
                            startIcon={<DeleteSweepIcon />}
                            onClick={() => {
                                setClearError(null);
                                setClearSuccess(false);
                                setClearDialogOpen(true);
                            }}
                        >
                            Clear Data
                        </Button>
                    </Tooltip>
                </Box>
            )}

            <Grid container spacing={2} sx={{ mb: 3 }}>
                <Grid item xs={12} sm={6} md={3}>
                    <Card variant="outlined">
                        <CardContent>
                            <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                                <TouchAppIcon color="primary" sx={{ mr: 1 }} />
                                <Typography variant="body2" color="text.secondary">Total Opens</Typography>
                            </Box>
                            <Typography variant="h5">{totals.totalOpens}</Typography>
                        </CardContent>
                    </Card>
                </Grid>
                <Grid item xs={12} sm={6} md={3}>
                    <Card variant="outlined">
                        <CardContent>
                            <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                                <PeopleIcon color="primary" sx={{ mr: 1 }} />
                                <Typography variant="body2" color="text.secondary">Unique Users (sum across components)</Typography>
                            </Box>
                            <Typography variant="h5">{totals.uniqueUsersApprox}</Typography>
                        </CardContent>
                    </Card>
                </Grid>
                <Grid item xs={12} sm={6} md={3}>
                    <Card variant="outlined">
                        <CardContent>
                            <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                                <TrendingUpIcon color="primary" sx={{ mr: 1 }} />
                                <Typography variant="body2" color="text.secondary">Most Opened</Typography>
                            </Box>
                            <Typography variant="h6">
                                {totals.mostOpened ? totals.mostOpened.componentName : '—'}
                            </Typography>
                            {totals.mostOpened && (
                                <Typography variant="body2" color="text.secondary">
                                    {totals.mostOpened.opens} opens
                                </Typography>
                            )}
                        </CardContent>
                    </Card>
                </Grid>
                <Grid item xs={12} sm={6} md={3}>
                    <Card variant="outlined">
                        <CardContent>
                            <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                                <EditIcon color="primary" sx={{ mr: 1 }} />
                                <Typography variant="body2" color="text.secondary">Components Tracked</Typography>
                            </Box>
                            <Typography variant="h5">{rows.length}</Typography>
                        </CardContent>
                    </Card>
                </Grid>
            </Grid>

            <Paper variant="outlined" sx={{ mb: 3 }}>
                <TableContainer>
                    <Table size="small">
                        <TableHead>
                            <TableRow>
                                <TableCell>Component</TableCell>
                                <TableCell>Type</TableCell>
                                <TableCell>Feature</TableCell>
                                <TableCell align="right">Opens</TableCell>
                                <TableCell align="right">Unique Users</TableCell>
                                <TableCell>Last Used</TableCell>
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {sortedRows.length === 0 ? (
                                <TableRow>
                                    <TableCell colSpan={6} align="center">
                                        <Typography variant="body2" color="text.secondary" sx={{ py: 2 }}>
                                            No usage data recorded for this range.
                                        </Typography>
                                    </TableCell>
                                </TableRow>
                            ) : (
                                sortedRows.map((row) => (
                                    <TableRow key={row.componentName}>
                                        <TableCell>{row.componentName}</TableCell>
                                        <TableCell>
                                            <Chip
                                                size="small"
                                                label={row.componentType}
                                                color={row.componentType === 'editor' ? 'primary' : 'default'}
                                                variant="outlined"
                                            />
                                        </TableCell>
                                        <TableCell>{row.feature || '—'}</TableCell>
                                        <TableCell align="right">{row.opens}</TableCell>
                                        <TableCell align="right">{row.uniqueUsers}</TableCell>
                                        <TableCell>
                                            {row.lastUsed ? new Date(row.lastUsed).toLocaleString() : '—'}
                                        </TableCell>
                                    </TableRow>
                                ))
                            )}
                        </TableBody>
                    </Table>
                </TableContainer>
            </Paper>

            <Dialog
                open={clearDialogOpen}
                onClose={() => setClearDialogOpen(false)}
                aria-labelledby="clear-usage-dialog-title"
            >
                <DialogTitle id="clear-usage-dialog-title" sx={{ color: 'error.main' }}>
                    Clear All Usage Tracking Data
                </DialogTitle>
                <DialogContent>
                    <DialogContentText sx={{ mb: 2 }}>
                        This will permanently delete <strong>all recorded dialog and editor open events</strong> from the database.
                    </DialogContentText>

                    <Alert severity="warning" sx={{ mt: 2 }}>
                        This action cannot be undone.
                    </Alert>

                    {clearError && (
                        <Alert severity="error" sx={{ mt: 2 }}>
                            {clearError}
                        </Alert>
                    )}

                    {clearSuccess && (
                        <Alert severity="success" sx={{ mt: 2 }}>
                            All usage tracking data has been successfully cleared.
                        </Alert>
                    )}
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setClearDialogOpen(false)} disabled={clearing}>
                        Cancel
                    </Button>
                    <Button
                        variant="contained"
                        color="error"
                        onClick={handleClearData}
                        disabled={clearing || clearSuccess}
                        startIcon={clearing ? <CircularProgress size={16} color="inherit" /> : <DeleteSweepIcon />}
                    >
                        {clearing ? 'Clearing...' : 'Clear All Data'}
                    </Button>
                </DialogActions>
            </Dialog>
        </Box>
    );
}
